import React, { useState, useEffect, useRef } from 'react'
import { X, Download, ExternalLink, ZoomIn, ZoomOut } from 'lucide-react'
import { isPdfFile, isImageFile } from '../utils/fileUtils'
import PDFViewer from './PDFViewer'

interface FilePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  fileUrl: string
  fileName: string
  onDownload?: () => void
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileUrl,
  fileName,
  onDownload
}) => {
  // Состояния для зума изображений
  const [scale, setScale] = useState<number>(1.0)
  const [isZooming, setIsZooming] = useState<boolean>(false)
  const [tempScale, setTempScale] = useState<number>(1.0)
  
  // Touch gestures
  const [pinchStartDistance, setPinchStartDistance] = useState<number | null>(null)
  const [pinchStartScale, setPinchStartScale] = useState<number>(1.0)
  const lastTapRef = useRef<number>(0)
  const imageContainerRef = useRef<HTMLDivElement>(null)

  // Drag and pan для увеличенных изображений
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  // Функции зума
  const handleZoomIn = () => {
    setScale(prev => Math.min(5.0, Number((prev + 0.2).toFixed(2))))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(0.5, Number((prev - 0.2).toFixed(2))))
  }

  const handleResetZoom = () => {
    setScale(1.0)
    setTempScale(1.0)
    setIsZooming(false)
    setPanOffset({ x: 0, y: 0 }) // Сбрасываем смещение при сбросе зума
    setIsDragging(false)
  }

  // Вспомогательные функции
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
  const getDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.hypot(dx, dy)
  }

  // Сброс зума при смене файла
  useEffect(() => {
    if (isOpen) {
      handleResetZoom()
    }
  }, [fileName, isOpen])

  // Блокировка прокрутки body при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      // Сохраняем текущие стили и позицию скролла
      const originalOverflow = document.body.style.overflow
      const originalPosition = document.body.style.position
      const originalWidth = document.body.style.width
      const originalHeight = document.body.style.height
      const originalTop = document.body.style.top
      const scrollY = window.scrollY
      
      // Блокируем прокрутку с сохранением позиции
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.height = '100%'
      
      // Восстанавливаем при закрытии или размонтировании
      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.position = originalPosition
        document.body.style.width = originalWidth
        document.body.style.height = originalHeight
        document.body.style.top = originalTop
        
        // Восстанавливаем позицию скролла
        if (scrollY > 0) {
          window.scrollTo(0, scrollY)
        }
      }
    }
  }, [isOpen])

  // Дополнительная очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      // Принудительно восстанавливаем стили body при размонтировании
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
      document.body.style.top = ''
      
      // Убеждаемся, что скролл работает
      setTimeout(() => {
        document.body.style.overflow = ''
        document.body.style.position = ''
      }, 0)
    }
  }, [])

  // Обработчик клавиши Escape для закрытия модального окна
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])
  // Для PDF файлов используем специальный PDFViewer
  if (isPdfFile(undefined, fileName)) {
    return (
      <PDFViewer
        isOpen={isOpen}
        onClose={onClose}
        fileUrl={fileUrl}
        fileName={fileName}
        onDownload={onDownload}
      />
    )
  }

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const renderFileContent = () => {
    if (isImageFile(undefined, fileName)) {
      // Изображение с зумом и лоадером
      return (
        <div className="w-full h-full flex items-center justify-center">
          {/* Лоадер показывается пока изображение загружается */}
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Загрузка изображения...</p>
            </div>
          </div>
          
          {/* Контейнер для плавного CSS-масштабирования */}
          <div
            style={{
              transform: isZooming 
                ? `scale(${tempScale}) translate(${panOffset.x}px, ${panOffset.y}px)` 
                : `scale(${scale}) translate(${panOffset.x}px, ${panOffset.y}px)`,
              transformOrigin: 'center center',
              transition: isZooming || isDragging ? 'none' : 'transform 0.2s ease-out',
            }}
            onDoubleClick={(e) => {
              // Double-click на десктопе: увеличение/сброс
              e.preventDefault()
              if (scale <= 1.1) {
                setScale(2.0)
                setTempScale(2.0)
              } else {
                handleResetZoom()
              }
            }}
          >
            {/* Изображение */}
            <img
              src={fileUrl}
              alt={fileName}
              className="max-w-full max-h-full object-contain shadow-lg"
              style={{ 
                maxHeight: '90vh'
              }}
              onLoad={(e) => {
                // Скрываем лоадер когда изображение загрузилось
                const target = e.target as HTMLImageElement;
                const container = target.closest('.w-full');
                const loader = container?.querySelector('.absolute') as HTMLElement;
                if (loader) loader.style.display = 'none';
              }}
              onError={(e) => {
                // При ошибке показываем fallback без innerHTML
                const target = e.target as HTMLImageElement;
                const container = target.closest('.w-full');
                const loader = container?.querySelector('.absolute') as HTMLElement;
                if (loader) {
                  // Безопасно заменяем содержимое
                  loader.className = 'absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg';
                  loader.innerHTML = '';
                  
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'text-center';
                  
                  const errorIcon = document.createElement('div');
                  errorIcon.className = 'text-red-500 text-6xl mb-4';
                  errorIcon.textContent = '⚠️';
                  
                  const errorText = document.createElement('p');
                  errorText.className = 'text-red-600 mb-4';
                  errorText.textContent = 'Не удалось загрузить изображение';
                  
                  const errorButton = document.createElement('button');
                  errorButton.className = 'px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors';
                  errorButton.textContent = 'Открыть в новой вкладке';
                  errorButton.onclick = () => window.open(fileUrl, '_blank');
                  
                  errorDiv.appendChild(errorIcon);
                  errorDiv.appendChild(errorText);
                  errorDiv.appendChild(errorButton);
                  loader.appendChild(errorDiv);
                }
                target.style.display = 'none';
              }}
            />
          </div>
        </div>
      )
    } else {
      // For other file types, show a preview message
      return (
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">📄</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{fileName}</h3>
          <p className="text-gray-600 mb-6">Предпросмотр недоступен для данного типа файла</p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={onDownload}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={16} className="mr-2" />
              Скачать
            </button>
            <button
              onClick={() => window.open(fileUrl, '_blank')}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ExternalLink size={16} className="mr-2" />
              Открыть в новой вкладке
            </button>
          </div>
        </div>
      )
    }
  }

  return (
    <div
      className="file-preview-modal fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      style={{
        paddingTop: 'max(env(safe-area-inset-top), 0px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
        paddingLeft: 'max(env(safe-area-inset-left), 0px)',
        paddingRight: 'max(env(safe-area-inset-right), 0px)',
      }}
      onClick={handleBackdropClick}
    >
      {/* Модальное окно */}
      <div className="relative w-full h-full max-w-7xl max-h-full flex flex-col m-2 md:m-4">
        {/* Заголовок с кнопками управления */}
        <div className="flex items-center justify-between mb-2 px-3 md:px-4 py-2 bg-white/95 backdrop-blur-sm rounded-t-lg shadow-sm">
          <h2 className="text-sm md:text-lg font-semibold text-gray-900 truncate flex-1 mr-2 md:mr-4">
            {fileName}
          </h2>
          <div className="flex items-center space-x-1 md:space-x-2">
            {/* Кнопки зума только для изображений */}
            {isImageFile(undefined, fileName) && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                  title="Уменьшить"
                >
                  <ZoomOut size={16} className="md:w-5 md:h-5" />
                </button>
                
                <button
                  onClick={handleResetZoom}
                  className="text-xs md:text-sm text-gray-600 hover:text-gray-900 px-2 py-1 hover:bg-gray-100 rounded transition-colors"
                  title="Сбросить масштаб"
                >
                  {Math.round(scale * 100)}%
                </button>
                
                <button
                  onClick={handleZoomIn}
                  className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                  title="Увеличить"
                >
                  <ZoomIn size={16} className="md:w-5 md:h-5" />
                </button>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
              </>
            )}
            
            {onDownload && (
              <button
                onClick={onDownload}
                className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                title="Скачать файл"
              >
                <Download size={16} className="md:w-5 md:h-5" />
              </button>
            )}
            <button
              onClick={() => window.open(fileUrl, '_blank')}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title="Открыть в новой вкладке"
            >
              <ExternalLink size={16} className="md:w-5 md:h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title="Закрыть"
            >
              <X size={16} className="md:w-5 md:h-5" />
            </button>
          </div>
        </div>

        {/* Контент файла */}
        <div 
          ref={imageContainerRef}
          className="flex-1 bg-gray-100 rounded-b-lg overflow-auto touch-pan-y"
          style={{
            minHeight: 0,
            touchAction: 'manipulation',
            cursor: scale > 1.1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onMouseDown={(e) => {
            if (!isImageFile(undefined, fileName) || scale <= 1.1) return;
            
            e.preventDefault()
            setIsDragging(true)
            setDragStart({ x: e.clientX, y: e.clientY })
          }}
          onMouseMove={(e) => {
            if (!isImageFile(undefined, fileName) || !isDragging || !dragStart || scale <= 1.1) return;
            
            e.preventDefault()
            const deltaX = e.clientX - dragStart.x
            const deltaY = e.clientY - dragStart.y
            
            setPanOffset(prev => ({
              x: prev.x + deltaX,
              y: prev.y + deltaY
            }))
            
            setDragStart({ x: e.clientX, y: e.clientY })
          }}
          onMouseUp={() => {
            setIsDragging(false)
            setDragStart(null)
          }}
          onMouseLeave={() => {
            setIsDragging(false)
            setDragStart(null)
          }}
          onTouchStart={(e) => {
            if (!isImageFile(undefined, fileName)) return;
            
            if (e.touches.length === 2) {
              // Pinch-to-zoom
              const dist = getDistance(e.touches)
              setPinchStartDistance(dist)
              setPinchStartScale(scale)
              setTempScale(scale)
              setIsZooming(true)
            } else if (e.touches.length === 1 && scale > 1.1) {
              // Single touch для перетаскивания увеличенного изображения
              setIsDragging(true)
              setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
            }
          }}
          onTouchMove={(e) => {
            if (!isImageFile(undefined, fileName)) return;
            
            if (e.touches.length === 2 && pinchStartDistance) {
              // Плавное масштабирование через CSS transform
              e.preventDefault()
              const newDist = getDistance(e.touches)
              const ratio = newDist / pinchStartDistance
              const newTempScale = clamp(Number((pinchStartScale * ratio).toFixed(2)), 0.5, 5.0)
              setTempScale(newTempScale)
            } else if (e.touches.length === 1 && isDragging && dragStart && scale > 1.1) {
              // Перетаскивание одним пальцем для увеличенного изображения
              e.preventDefault()
              const deltaX = e.touches[0].clientX - dragStart.x
              const deltaY = e.touches[0].clientY - dragStart.y
              
              setPanOffset(prev => ({
                x: prev.x + deltaX,
                y: prev.y + deltaY
              }))
              
              setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
            }
          }}
          onTouchEnd={(e) => {
            if (!isImageFile(undefined, fileName)) return;
            
            if (pinchStartDistance) {
              // Завершение pinch-to-zoom
              setPinchStartDistance(null)
              setScale(Number(tempScale.toFixed(2)))
              setIsZooming(false)
            }
            
            // Завершение перетаскивания
            if (isDragging) {
              setIsDragging(false)
              setDragStart(null)
            }
            
            // Double-tap для сброса масштаба (только если не было перетаскивания)
            if (!isDragging) {
              const now = Date.now()
              const timeDiff = now - lastTapRef.current
              const isMobile = 'ontouchstart' in window
              if (isMobile && timeDiff < 300 && e.touches.length === 0) {
                handleResetZoom()
                lastTapRef.current = 0
              } else {
                lastTapRef.current = now
              }
            }
          }}
        >
          <div className="flex items-center justify-center min-h-full p-4">
            {renderFileContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilePreviewModal
